import { useAuth0 } from '@auth0/auth0-react';
import { RuleProperties } from '@react-awesome-query-builder/mui';
import { useAtomValue, useSetAtom } from 'jotai';

import { domainCodeAtom } from '@australiangreens/ag-internal-components';
import { SearchFilter } from '@/types/searchFilterTypes';
import { isValidationDialogOpenAtom, templateValidationErrorsAtom } from '@/pages/AdvancedSearchListCreatorPage/ValidationDialog';
import { FilterMappers } from './FilterMappers';
import type { MapQbRulePropertiesToSearchFilterOptions } from './filterMapperTypes';

export type { MapQbRulePropertiesToSearchFilterOptions } from './filterMapperTypes';

const DISPLAY_NAMES: Record<string, string> = {
  hasPhone: 'Has Phone',
  hasEmail: 'Has Email',
  geometryIds: 'Locations',
  tagIds: 'Tags',
  groupIds: 'Groups',
  supporterStatuses: 'Supporter Statuses',
  pathwaySteps: 'Pathway Steps',
  listIds: 'Lists',
  age: 'Age',
  membership: 'Membership',
  task: 'Tasks',
  mailing: 'Mailing',
  roster: 'Roster',
  eventAttendance: 'Event Attendance',
  engagementHistory: 'Engagement History',
  callingStatus: 'Calling Status',
  commsPreferences: 'Comms Preferences',
  sa1: 'SA1',
  branches: 'Branches',
  locations: 'Locations',
  lists: 'Lists',
  tags: 'Tags',
  groups: 'Groups',
  campaigns: 'Campaigns',
  mailings: 'Mailings',
  users: 'Users',
  events: 'Events',
  eventCampaigns: 'Event Campaigns',
  eventTypes: 'Event Types',
};

function displayName(name: string): string {
  return DISPLAY_NAMES[name] ?? name;
}

/** Convert the properties of a single rule to a search filter for use in list
 * manager, suitable to be used in a search tree */
export function mapQbRulePropertiesToSearchFilter(
  props: RuleProperties,
  options: MapQbRulePropertiesToSearchFilterOptions
): SearchFilter {
  if (!props.field) throw new Error(`Missing 'field' property`);
  const field = props.field as string;

  if (field in FilterMappers) {
    return FilterMappers[field].toFilter(props, options);
  } else {
    throw new Error(`Invalid rule field '${field}'`);
  }
}

/** Convert a single search filter to properties of a rule suitable to be used
 * in a Json tree in the query builder */
export function useMapQbRulePropertiesFromSearchFilter() {
  const { getAccessTokenSilently, user } = useAuth0();
  const domainCode = useAtomValue(domainCodeAtom);
  const setIsValidationDialogOpen = useSetAtom(isValidationDialogOpenAtom);
  const setTemplateValidationErrors = useSetAtom(templateValidationErrorsAtom);

  // For LIST-1050

  return async (filter: SearchFilter): Promise<RuleProperties> => {
    const field = filter.name;

    if (field in FilterMappers) {
      const ruleProperties = await FilterMappers[field].fromFilter(filter, {
        token: await getAccessTokenSilently(),
        domainCode,
        userSub: user?.sub ?? '',
      });
      const errors = ruleProperties.value[0].errors;
      if (errors?.length > 0) {
        setIsValidationDialogOpen(true);
        setTemplateValidationErrors((prev) => [
          ...prev,
          ...errors.map((e: { field: string }) => ({
            filterName: displayName(field),
            fieldName: displayName(e.field),
          })),
        ]);
      }

      return ruleProperties;
    } else {
      throw new Error(`Invalid filter name '${field}'`);
    }
  };
}
